import React, { useEffect, useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import RootState from '../../core/root-state'
import { Permission, roleType } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import {
  strings,
  monthlySalesDataContent,
  dailySalesDataContent,
  sbjSalesDataContent,
} from '../components/sales-data/sales-data-content'
import SalesDataFrame from '../components/sales-data/sales-data-frame'
import { SalesData, salesDataActions } from '../../core/sales-data'
import BrandFilter from '../components/common/brand-filter'
import { Brand, BrandCode, BRAND_CODES, brandsActions } from '../../core/brands'

interface Props {
  monthlySalesData: SalesData
  fetchMonthlySalesData: () => void
  dailySalesData: SalesData
  fetchDailySalesData: () => void
  sbjSalesData: SalesData
  fetchSBJSalesData: () => void
  fetchBrands: () => void
  userRole: roleType
  brands: Brand[]
}

const SalesDataScreen: React.FC<Props> = (props: Props) => {
  const {
    monthlySalesData,
    fetchMonthlySalesData,
    dailySalesData,
    fetchDailySalesData,
    sbjSalesData,
    fetchSBJSalesData,
    fetchBrands,
    userRole,
    brands,
  } = props

  const isAdmin = userRole === roleType.ADMIN
  const isSbjUser =
    userRole === roleType.SBJ_ADMIN || userRole === roleType.SBJ_SHOP

  // デフォルトブランドを設定
  const defaultBrand = isSbjUser ? BRAND_CODES.SBJ : BRAND_CODES.ROOTC
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(defaultBrand)
  const [isLoading, setIsLoading] = useState(false)

  // 初回ロード時にブランド一覧とデータ取得
  useEffect(() => {
    // ブランド一覧を取得
    fetchBrands()

    // 売上データを取得
    if (isSbjUser) {
      fetchSBJSalesData()
    } else if (selectedBrand === BRAND_CODES.ROOTC) {
      fetchMonthlySalesData()
      fetchDailySalesData()
    } else if (selectedBrand === BRAND_CODES.SBJ) {
      fetchSBJSalesData()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ブランドフィルター変更時の処理
  const handleBrandChange = (brand: BrandCode) => {
    setSelectedBrand(brand)
  }

  // 絞り込みボタンクリック時の処理
  const handleFilterClick = () => {
    setIsLoading(true)
    if (selectedBrand === BRAND_CODES.ROOTC) {
      fetchMonthlySalesData()
      fetchDailySalesData()
    } else if (selectedBrand === BRAND_CODES.SBJ) {
      fetchSBJSalesData()
    }
    setTimeout(() => setIsLoading(false), 500)
  }

  // 表示するコンテンツを決定
  const renderSalesDataContent = () => {
    if (selectedBrand === BRAND_CODES.SBJ) {
      return (
        <SalesDataFrame
          title={sbjSalesDataContent.title}
          salesData={sbjSalesData}
          height={sbjSalesDataContent.height}
        />
      )
    } else {
      // RootC または未選択の場合は月次と日次を表示
      return (
        <>
          <SalesDataFrame
            title={monthlySalesDataContent.title}
            salesData={monthlySalesData}
            height={monthlySalesDataContent.height}
          />
          <SalesDataFrame
            title={dailySalesDataContent.title}
            salesData={dailySalesData}
            height={dailySalesDataContent.height}
          />
        </>
      )
    }
  }

  return (
    <Layout location="sales_data">
      <Wrapper title={strings.title}>
        <BrandFilter
          currentBrand={selectedBrand}
          onBrandChange={handleBrandChange}
          onFilterClick={handleFilterClick}
          isAdminOnly={isAdmin}
          brands={brands}
          isLoading={isLoading}
        />
        {renderSalesDataContent()}
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    monthlySalesData: state.salesData.monthlySalesData,
    dailySalesData: state.salesData.dailySalesData,
    sbjSalesData: state.salesData.sbjSalesData,
    userRole: state.auth0.role,
    brands: state.brands.brands,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchMonthlySalesData: () =>
    dispatch(salesDataActions.fetchMonthlySalesData()),
  fetchDailySalesData: () => dispatch(salesDataActions.fetchDailySalesData()),
  fetchSBJSalesData: () => dispatch(salesDataActions.fetchSBJSalesData()),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedSalesDataScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(SalesDataScreen)

export default withPermissionCheck(ConnectedSalesDataScreen, {
  requiredPermission: [
    Permission.ROOTC_SALES_DATA_READ,
    Permission.SBJ_SALES_DATA_READ,
  ],
  requireAny: true,
  redirectTo: '/404',
})
